import { Injectable, computed, inject, signal } from "@angular/core";
import { SimService } from "./sim.service";

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

export type Gear = -1 | 0 | 1;
export type SteeringControlKind = "wheel" | "tiller";
export type EngineControlKind = "inboard" | "outboard";

const DEFAULT_HELM_WHEEL_LOCK_DEG = 45 * 10.3;

/**
 * UI source-of-truth for boat helm + engine. Widgets read/write here; the
 * store forwards to the simulation through {@link SimService.setBoatControls}.
 *
 * Engine model: GUI separates `gear ∈ {-1, 0, +1}` (R/N/F) from `gas ∈ [0, 1]`
 * (throttle grip), and combines them as `throttle = gear * gas`. The sim
 * itself only knows the combined `throttle ∈ [-1, +1]` scalar — that match
 * is an intentional simplification, the split exists only for ergonomic UI.
 */
@Injectable({ providedIn: "root" })
export class BoatControlsStore {
  private readonly sim = inject(SimService);

  readonly steer = signal(0);
  readonly engineSteer = signal(0);
  readonly steering = signal<SteeringControlKind>("wheel");
  readonly engineType = signal<EngineControlKind>("inboard");
  readonly engineSteeringCoupled = signal(true);
  readonly helmWheelLockDeg = signal(DEFAULT_HELM_WHEEL_LOCK_DEG);
  readonly gear = signal<Gear>(0);
  readonly gas = signal(0);

  readonly throttle = computed(() => this.gear() * this.gas());
  readonly showOutboardSteer = computed(
    () => this.engineType() === "outboard" && !this.engineSteeringCoupled(),
  );

  /**
   * Push live boat values back into the UI signals WITHOUT forwarding them
   * to the sim (would create a feedback loop). Called every frame from
   * SimService's poll loop so the wheel/lever follow keyboard input.
   */
  syncFromSim(
    throttle: number,
    steer: number,
    engineSteer = steer,
    helmWheelLockDeg = DEFAULT_HELM_WHEEL_LOCK_DEG,
    steering: SteeringControlKind = "wheel",
    engineType: EngineControlKind = "inboard",
    engineSteeringCoupled = true,
    gas: number = Math.abs(throttle),
    gear: Gear = throttle > 1e-3 ? 1 : throttle < -1e-3 ? -1 : 0,
  ): void {
    if (Math.abs(this.steer() - steer) > 1e-3) {
      this.steer.set(Math.max(-1, Math.min(1, steer)));
    }
    if (Math.abs(this.helmWheelLockDeg() - helmWheelLockDeg) > 1e-3) {
      this.helmWheelLockDeg.set(Math.max(1, helmWheelLockDeg));
    }
    if (Math.abs(this.engineSteer() - engineSteer) > 1e-3) {
      this.engineSteer.set(clamp(engineSteer, -1, 1));
    }
    if (this.steering() !== steering) {
      this.steering.set(steering);
    }
    if (this.engineType() !== engineType) {
      this.engineType.set(engineType);
    }
    if (this.engineSteeringCoupled() !== engineSteeringCoupled) {
      this.engineSteeringCoupled.set(engineSteeringCoupled);
    }
    // Gas + gear come from the sim as independent fields, so the UI can
    // mirror them without re-derivation. Two values must compare loosely
    // (the gas signal is smoothed by the input layer's 1st-order lag).
    const clampedGas = Math.max(0, Math.min(1, gas));
    if (Math.abs(this.gas() - clampedGas) > 1e-3) {
      this.gas.set(clampedGas);
    }
    if (this.gear() !== gear) {
      this.gear.set(gear);
    }
  }

  setSteer(v: number): void {
    this.steer.set(clamp(v, -1, 1));
    this.sim.setBoatControls({ steer: this.steer() });
  }

  setEngineSteer(v: number): void {
    this.engineSteer.set(clamp(v, -1, 1));
    this.sim.setBoatControls({ engineSteer: this.engineSteer() });
  }

  setGear(g: Gear): void {
    // Gas and gear are independent — switching gear does NOT touch the
    // grip position. Sim mirrors the same rule.
    this.gear.set(g);
    this.sim.setBoatControls({ gear: g });
  }

  setGas(v: number): void {
    const g = clamp(v, 0, 1);
    this.gas.set(g);
    this.sim.setBoatControls({ gas: g });
  }

  /** Single-lever (inboard) drive: -1..+1 mapped directly. */
  setLever(v: number): void {
    const c = clamp(v, -1, 1);
    let gear: Gear;
    let gas: number;
    if (c > 1e-3) {
      gear = 1;
      gas = c;
    } else if (c < -1e-3) {
      gear = -1;
      gas = -c;
    } else {
      gear = 0;
      gas = 0;
    }
    this.gear.set(gear);
    this.gas.set(gas);
    this.sim.setBoatControls({ gear, gas });
  }

  /** Hand controls back to the keyboard / sim defaults. */
  release(): void {
    this.steer.set(0);
    this.engineSteer.set(0);
    this.gear.set(0);
    this.gas.set(0);
    this.sim.setBoatControls({
      throttle: null,
      gas: null,
      gear: null,
      steer: null,
      engineSteer: null,
    });
  }
}
